import React, { useState } from 'react';

import { ChevronRight } from 'lucide-react';

import type { PricingConfiguration } from '../../../types';

interface BasicInfoStepProps {
	configuration: PricingConfiguration;
	onUpdate: (updates: Partial<PricingConfiguration>) => void;
	onNext: () => void;
	onCancel?: () => void;
	hideNavigation?: boolean;
	// Override the flag checkbox. Rate configs use `is_default`; revenue configs
	// use `is_active`. When these are passed, the checkbox drives the caller's
	// field instead of defaulting to `configuration.is_default`.
	flagChecked?: boolean;
	onFlagChange?: (checked: boolean) => void;
	flagLabel?: string;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({
	configuration,
	onUpdate,
	onNext,
	onCancel,
	hideNavigation = false,
	flagChecked,
	onFlagChange,
	flagLabel,
}) => {
	const [nameError, setNameError] = useState('');

	// Default to the rate-config `is_default` field when no override is supplied.
	// Coerce to false so the checkbox is always controlled (avoids React's
	// "uncontrolled -> controlled" warning when both are undefined).
	const isFlagChecked = flagChecked ?? configuration.is_default ?? false;
	const handleFlagChange = (checked: boolean) =>
		onFlagChange
			? onFlagChange(checked)
			: onUpdate({ is_default: checked });
	const checkboxLabel = flagLabel ?? 'Set as Default Configuration';

	const handleNext = () => {
		// Validate required fields
		if (!configuration.name.trim()) {
			setNameError('Configuration name is required');
			return;
		}
		setNameError('');
		onNext();
	};

	const handleNameChange = (name: string) => {
		onUpdate({ name });
		if (nameError && name.trim()) {
			setNameError('');
		}
	};

	return (
		<div className="space-y-8">
			<div className="text-center mb-12">
				<h3 className="text-2xl font-bold text-gray-900 mb-3">Basic details</h3>
				<p className="text-gray-600 text-lg">
					Name and save your pricing configuration for future use
				</p>
			</div>

			<div className="max-w-lg mx-auto space-y-8">
				{/* Configuration Name */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-2">
						Configuration Name <span className="text-red-500">*</span>
					</label>
					<input
						type="text"
						value={configuration.name}
						onChange={(e) => handleNameChange(e.target.value)}
						placeholder="Configuration Name"
						className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500 text-gray-900 ${
							nameError ? 'border-red-300 bg-red-50' : 'border-gray-300'
						} disabled:opacity-50 disabled:cursor-not-allowed`}
					/>
					{nameError && (
						<p className="mt-1 text-sm text-red-600">{nameError}</p>
					)}
				</div>

				{/* Description */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-2">
						Description
					</label>
					<textarea
						value={configuration.description}
						onChange={(e) => onUpdate({ description: e.target.value })}
						placeholder="Describe the configuration"
						rows={3}
						className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500 text-gray-900 resize-none"
					/>
				</div>

				{/* Default / Active Configuration Checkbox */}
				<div className="flex items-center">
					<input
						type="checkbox"
						id="configFlag"
						checked={isFlagChecked}
						onChange={(e) => handleFlagChange(e.target.checked)}
						className="h-4 w-4 text-blue-600 focus:ring-2 focus:ring-blue-500 border-gray-300 rounded"
					/>
					<label
						htmlFor="configFlag"
						className="ml-2 block text-sm text-gray-900"
					>
						{checkboxLabel}
					</label>
				</div>
			</div>

			{/* Navigation - Only show if not hidden */}
			{!hideNavigation && (
				<div className="flex justify-between items-center pt-8 mt-12">
					<button
						onClick={onCancel}
						className="text-gray-500 hover:text-gray-700 transition-colors font-medium"
					>
						Cancel
					</button>
					<div className="flex items-center text-gray-500">
						<span className="text-sm font-medium">Step 1 of 4</span>
					</div>
					<button
						onClick={handleNext}
						className="flex items-center px-8 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors font-medium shadow-lg"
					>
						Next
						<ChevronRight className="w-5 h-5 ml-2" />
					</button>
				</div>
			)}
		</div>
	);
};

export default BasicInfoStep;
